'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { createClient } from '@/lib/supabase/client';
import { tenantConfigService } from '@/lib/services/tenantConfigService';

export interface PlatformCentreSettings {
  centreHrEnabled: boolean;
  centrePayrollEnabled: boolean;
  centreShiftSchedulingEnabled: boolean;
  centreEmployeeTransportEnabled: boolean;
  centreWarehouseEnabled: boolean;
  centreClientsSitesEnabled: boolean;
  payrollMode: 'full_payroll' | 'gross_only';
}

interface PlatformSettingsContextValue {
  settings: PlatformCentreSettings;
  loading: boolean;
  refresh: () => void;
}

// Defaults — all enabled so the UI is fully visible before settings load
const DEFAULT_SETTINGS: PlatformCentreSettings = {
  centreHrEnabled: true,
  centrePayrollEnabled: true,
  centreShiftSchedulingEnabled: true,
  centreEmployeeTransportEnabled: true,
  centreWarehouseEnabled: false,
  centreClientsSitesEnabled: true,
  payrollMode: 'full_payroll',
};

function toCamel(obj: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    result[key.replace(/_([a-z])/g, (_, c) => c.toUpperCase())] = value;
  }
  return result;
}

const PlatformSettingsContext = createContext<PlatformSettingsContextValue>({
  settings: DEFAULT_SETTINGS,
  loading: true,
  refresh: () => {},
});

export function PlatformSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<PlatformCentreSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const cid = await tenantConfigService.getCompanyId();
      if (!cid) { setLoading(false); return; }

      const supabase = createClient();
      const { data } = await supabase
        .from('platform_settings')
        .select('*')
        .eq('company_id', cid)
        .maybeSingle();

      if (data) {
        const camel = toCamel(data) as PlatformCentreSettings;
        setSettings({
          centreHrEnabled: camel.centreHrEnabled ?? true,
          centrePayrollEnabled: camel.centrePayrollEnabled ?? true,
          centreShiftSchedulingEnabled: camel.centreShiftSchedulingEnabled ?? true,
          centreEmployeeTransportEnabled: camel.centreEmployeeTransportEnabled ?? true,
          centreWarehouseEnabled: camel.centreWarehouseEnabled ?? false,
          centreClientsSitesEnabled: camel.centreClientsSitesEnabled ?? true,
          payrollMode: camel.payrollMode ?? 'full_payroll',
        });
      }
    } catch {
      // Silently fall back to defaults — platform stays functional
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <PlatformSettingsContext.Provider value={{ settings, loading, refresh: load }}>
      {children}
    </PlatformSettingsContext.Provider>
  );
}

export function usePlatformSettings() {
  return useContext(PlatformSettingsContext);
}
